import React from "react";

interface OsiScoreDisplayProps {
  scores: Record<string, any>;
  interpretations: Record<string, any>;
}

const OsiScoreDisplay: React.FC<OsiScoreDisplayProps> = ({ 
  scores, 
  interpretations 
}) => {
  // OSI has scores organized in categories (ORQ, PSQ, PRQ)
  const categories = {
    ORQ: {
      title: "Cuestionario de Roles Ocupacionales (ORQ)",
      description: "Mide estresores relacionados con el trabajo y el ambiente laboral",
      isResource: false,
    },
    PSQ: {
      title: "Cuestionario de Tensión Personal (PSQ)",
      description: "Mide la tensión o strain experimentada por la persona",
      isResource: false,
    },
    PRQ: {
      title: "Cuestionario de Recursos Personales (PRQ)",
      description: "Mide recursos que ayudan a moderar los efectos del estrés y la tensión",
      isResource: true,
    },
  };

  // Helper function to get color based on interpretation text
  const getColorFromInterpretation = (text: string): string => {
    if (!text) return "text-gray-500";
    
    const lowerText = text.toLowerCase();
    if (lowerText.includes("bajo") || lowerText.includes("muy bajo")) {
      return "text-green-500";
    } else if (lowerText.includes("normal") || lowerText.includes("moderado")) {
      return "text-yellow-500";
    } else if (lowerText.includes("alto")) {
      return "text-red-500";
    }
    return "text-gray-500";
  };

  // Helper function to get background color based on interpretation text
  const getBgColorFromInterpretation = (text: string): string => {
    if (!text) return "bg-gray-300";
    
    const lowerText = text.toLowerCase();
    if (lowerText.includes("bajo") || lowerText.includes("muy bajo")) {
      return "bg-green-500";
    } else if (lowerText.includes("normal") || lowerText.includes("moderado")) {
      return "bg-yellow-500";
    } else if (lowerText.includes("alto")) {
      return "bg-red-500";
    }
    return "bg-gray-300";
  };

  // Check if we have OSI's nested structure
  if (!scores.ORQ && !scores.PSQ && !scores.PRQ) {
    return (
      <div className="text-center py-4">
        <p className="text-gray-600 dark:text-gray-400">
          Estructura de puntuaciones inválida para OSI
        </p>
      </div>
    );
  }

  // Helper to determine percentile value
  const getPercentile = (key: string) => {
    // First try to get percentile from the percentiles object
    if (interpretations.percentiles && interpretations.percentiles[key] !== undefined) {
      return interpretations.percentiles[key];
    }
    
    // If not found, try to determine a value based on interpretation text
    const categoryKey = Object.keys(categories).find(cat => 
      scores[cat] && scores[cat][key]
    );
    
    if (!categoryKey) return 0;
    
    let interpretationText = "";
    if (interpretations[categoryKey] && interpretations[categoryKey][key]) {
      interpretationText = interpretations[categoryKey][key];
      
      // Estimate percentile from interpretation text
      const lowerText = interpretationText.toLowerCase();
      if (lowerText.includes("muy bajo") || lowerText.includes("bajos recursos")) {
        return 15;
      } else if (lowerText.includes("bajo")) {
        return 25;
      } else if (lowerText.includes("normal") || lowerText.includes("moderado")) {
        return 50;
      } else if (lowerText.includes("alto")) {
        return 85;
      }
    }
    
    return 0;
  };

  // Debug log
  console.log("OSI scores:", scores);
  console.log("OSI interpretations:", interpretations);

  return (
    <div className="space-y-8">
      {Object.entries(categories).map(([category, info]) => {
        const categoryScores = scores[category];
        if (!categoryScores) return null;

        return (
          <div key={category} className="mb-6">
            <h5 className="text-lg font-bold text-navy-700 dark:text-white mb-2">
              {info.title}
            </h5>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {info.description}
            </p>

            <div className="space-y-4">
              {Object.entries(categoryScores).map(([key, score]: [string, any]) => {
                // Get interpretation text if available
                let interpretationText = "";
                if (interpretations[category] && interpretations[category][key]) {
                  interpretationText = interpretations[category][key];
                }
                
                // Get percentile value
                const percentile = getPercentile(key);
                
                // Get bar width for progress
                let barWidth = `${percentile}%`;
                if (percentile === 0 && interpretationText) {
                  // If we have an interpretation but no percentile, make a visible bar
                  barWidth = interpretationText.toLowerCase().includes("bajo") ? "15%" : 
                             interpretationText.toLowerCase().includes("normal") ? "50%" : "85%";
                }
                
                return (
                  <div key={key} className="w-full">
                    <div className="flex flex-wrap justify-between items-center mb-1">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {score.name}
                      </span>
                      <div className="flex items-center">
                        <span className="text-xs text-gray-500 dark:text-gray-400 mr-2">
                          {percentile > 0 ? `${percentile}%` : ""}
                        </span>
                        <span className={`text-sm font-medium ${getColorFromInterpretation(interpretationText)}`}>
                          {interpretationText}
                        </span>
                      </div>
                    </div>
                    
                    <div className="w-full bg-gray-200 dark:bg-navy-700 rounded-full h-2.5">
                      <div 
                        className={`h-2.5 rounded-full ${getBgColorFromInterpretation(interpretationText)}`} 
                        style={{ width: barWidth }}
                      ></div>
                    </div>
                    
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {score.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Legend */}
      <div className="mt-8 pt-4 border-t border-gray-200 dark:border-gray-700">
        <h5 className="text-md font-bold text-navy-700 dark:text-white mb-3">
          Leyenda
        </h5>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h6 className="text-sm font-semibold mb-2">ORQ y PSQ</h6>
            <div className="space-y-2">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                <span className="text-xs">Bajo estrés/tensión (0-25%)</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
                <span className="text-xs">Estrés/tensión normal (26-75%)</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                <span className="text-xs">Alto estrés/tensión (76-100%)</span>
              </div>
            </div>
          </div>
          <div>
            <h6 className="text-sm font-semibold mb-2">PRQ</h6>
            <div className="space-y-2">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                <span className="text-xs">Bajos recursos (0-25%)</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
                <span className="text-xs">Recursos normales (26-75%)</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                <span className="text-xs">Recursos altamente desarrollados (76-100%)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OsiScoreDisplay; 